import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
    PlusLg, PencilSquare, Trash3, Images, Eye, EyeSlash, Search, Tag, CaretUpFill, CaretDownFill
} from 'react-bootstrap-icons';
import Pagination from '@mui/material/Pagination';
import AdminLayout, { isStaff } from '../AdminLayout';
import CarForm from './CarForm';
import CarImage from '../../../common/ui/CarImage';
import ConfirmModal from '../../../common/ui/ConfirmModal';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../../common/ui/StateBlock';
import notify, { currentLanguage } from '../../../common/ui/notify';
import language from '../../../data/language';
import {
    carTitle, coverImage, display, formatDate, formatMileage, formatPrice, isBlank
} from '../../../common/format';

const PAGE_SIZE = 15;
const SEARCH_DEBOUNCE_MS = 400;

const SORTABLE = {
    price: {asc: 'price_asc', desc: 'price_desc'},
    millage: {asc: 'millage_asc', desc: 'millage_desc'},
    year: {asc: 'year_asc', desc: 'year_desc'},
    id: {asc: 'oldest', desc: 'newest'}
};

const AdminCars = () => {
    const lang = currentLanguage();
    const strings = language[lang].admin;
    const marketStrings = language[lang].market;
    const common = language[lang].common;
    const user = useSelector((state) => state.user);
    // Small inline localisation for the hide/show feature (not in the shared file).
    const tl = (geo, en, ru, am) => ({ geo, en, ru, am }[lang] ?? geo);

    const [cars, setCars] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState('newest');
    const [status, setStatus] = useState('');
    const [keywordInput, setKeywordInput] = useState("");
    const [keyword, setKeyword] = useState("");
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [reload, setReload] = useState(0);

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [rowBusyId, setRowBusyId] = useState(null);

    const requestRef = useRef(0);
    const canDelete = user.role === 'admin';

    // Debounce the search box so typing does not fire a request per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (keywordInput !== keyword) {
                setKeyword(keywordInput);
                setPage(1);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [keywordInput, keyword]);

    const fetchCars = useCallback((signal) => {
        const requestId = ++requestRef.current;
        setLoading(true);
        setFailed(false);

        const params = {limit: PAGE_SIZE, page, sort};
        if (keyword) params.keyword = keyword;
        if (status) params.status = status;

        return axios.get('/api/cars', {params, signal})
            .then((res) => {
                if (requestId !== requestRef.current) return;
                setCars(Array.isArray(res.data.data) ? res.data.data : []);
                setTotal(Number(res.data.total) || 0);
                setTotalPages(Number(res.data.total_pages) || 0);
                // The API clamps an out-of-range page; mirror that locally.
                if (res.data.page && Number(res.data.page) !== page) setPage(Number(res.data.page));
                setLoading(false);
            })
            .catch((error) => {
                if (axios.isCancel(error) || error.name === 'CanceledError') return;
                if (requestId !== requestRef.current) return;
                setCars([]);
                setTotal(0);
                setFailed(true);
                setLoading(false);
            });
    }, [page, sort, keyword, status]);

    useEffect(() => {
        if (!isStaff(user)) return undefined;
        const controller = new AbortController();
        fetchCars(controller.signal);
        return () => controller.abort();
    }, [fetchCars, reload, user]);

    const refresh = () => setReload((prev) => prev + 1);

    const toggleSort = (column) => {
        const pair = SORTABLE[column];
        if (!pair) return;
        setSort((prev) => (prev === pair.desc ? pair.asc : pair.desc));
        setPage(1);
    };

    const sortIcon = (column) => {
        const pair = SORTABLE[column];
        if (!pair) return null;
        if (sort === pair.asc) return <CaretUpFill aria-hidden="true" />;
        if (sort === pair.desc) return <CaretDownFill aria-hidden="true" />;
        return null;
    };

    const toggleSold = async (car) => {
        setRowBusyId(car.id);
        try {
            await axios.put(`/api/cars/${car.id}/sold`, {sold: Number(car.sold) === 1 ? 0 : 1});
            notify.success(strings.status_changed);
            refresh();
        } catch (error) {
            notify.apiError(error);
        } finally {
            setRowBusyId(null);
        }
    };

    const toggleHidden = async (car) => {
        setRowBusyId(car.id);
        try {
            const res = await axios.put(`/api/cars/${car.id}/hidden`, { hidden: Number(car.hidden) === 1 ? 0 : 1 });
            notify.success(res.data && res.data.message ? res.data.message : strings.status_changed);
            refresh();
        } catch (error) {
            notify.apiError(error);
        } finally {
            setRowBusyId(null);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setRowBusyId(deleteTarget.id);
        try {
            await axios.delete(`/api/cars/${deleteTarget.id}`);
            notify.success(strings.car_deleted);
            setDeleteTarget(null);
            refresh();
        } catch (error) {
            notify.apiError(error);
        } finally {
            setRowBusyId(null);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const openEdit = (car, startOnImages = false) => {
        setEditing({...car, __startOnImages: startOnImages});
        setFormOpen(true);
    };

    const body = () => {
        if (loading) return <LoadingBlock label={common.loading} />;

        if (failed) {
            return (
                <ErrorBlock
                    title={common.error_title}
                    message={common.error_message}
                    retryLabel={common.retry}
                    onRetry={refresh}
                />
            );
        }

        if (cars.length === 0) {
            return (
                <EmptyBlock
                    title={keyword || status ? marketStrings.empty_title : strings.no_cars_title}
                    message={keyword || status ? marketStrings.empty_message : strings.no_cars_message}
                    action={
                        <button type="button" className="cx-btn cx-btn-primary" onClick={openCreate}>
                            <PlusLg aria-hidden="true" /> {strings.add_car}
                        </button>
                    }
                />
            );
        }

        return (
            <>
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th scope="col" className="admin-table-photo">{strings.table_photo}</th>
                                <th scope="col">{strings.table_car}</th>
                                <th scope="col" className="admin-hide-md">{strings.table_vin}</th>
                                <th scope="col">
                                    <button type="button" className="admin-sort-button" onClick={() => toggleSort('price')}>
                                        {strings.table_price} {sortIcon('price')}
                                    </button>
                                </th>
                                <th scope="col" className="admin-hide-sm">
                                    <button type="button" className="admin-sort-button" onClick={() => toggleSort('millage')}>
                                        {strings.table_mileage} {sortIcon('millage')}
                                    </button>
                                </th>
                                <th scope="col" className="admin-hide-lg">{strings.table_location}</th>
                                <th scope="col">{strings.table_status}</th>
                                <th scope="col" className="admin-hide-lg">{strings.table_date}</th>
                                <th scope="col" className="admin-table-actions-col">{strings.table_actions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cars.map((car) => {
                                const sold = Number(car.sold) === 1;
                                const hidden = Number(car.hidden) === 1;
                                const busy = rowBusyId === car.id;

                                return (
                                    <tr key={car.id} className={busy ? "is-busy" : ""}>
                                        <td className="admin-table-photo">
                                            <div className="admin-table-thumb">
                                                <CarImage src={coverImage(car)} alt="" />
                                            </div>
                                        </td>
                                        <td>
                                            <span className="admin-table-primary">{carTitle(car)}</span>
                                            <span className="admin-table-secondary admin-show-md">
                                                {isBlank(car.vin) ? "" : car.vin}
                                            </span>
                                        </td>
                                        <td className="admin-hide-md admin-mono">{display(car.vin)}</td>
                                        <td className="admin-table-price">{formatPrice(car.price, car.currency)}</td>
                                        <td className="admin-hide-sm">{isBlank(car.millage) ? "—" : formatMileage(car.millage)}</td>
                                        <td className="admin-hide-lg admin-table-secondary">{display(car.location)}</td>
                                        <td>
                                            <span className={`cx-badge ${sold ? "cx-badge-sold" : "cx-badge-sale"}`}>
                                                {sold ? marketStrings.status_sold : marketStrings.status_for_sale}
                                            </span>
                                            {hidden && (
                                                <span className="cx-badge cx-badge-neutral" style={{ marginLeft: 6 }}>
                                                    {tl("დამალული", "Hidden", "Скрыт", "Թաքցված")}
                                                </span>
                                            )}
                                        </td>
                                        <td className="admin-hide-lg admin-table-secondary">{formatDate(car.create_date)}</td>
                                        <td>
                                            <div className="admin-row-actions">
                                                <Link
                                                    to={`/car/${car.id}`}
                                                    className="admin-icon-button"
                                                    title={common.view}
                                                    aria-label={`${common.view}: ${carTitle(car)}`}
                                                >
                                                    <Eye aria-hidden="true" />
                                                </Link>
                                                <button
                                                    type="button"
                                                    className="admin-icon-button"
                                                    title={common.edit}
                                                    aria-label={`${common.edit}: ${carTitle(car)}`}
                                                    onClick={() => openEdit(car)}
                                                >
                                                    <PencilSquare aria-hidden="true" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="admin-icon-button"
                                                    title={strings.manage_images}
                                                    aria-label={`${strings.manage_images}: ${carTitle(car)}`}
                                                    onClick={() => openEdit(car, true)}
                                                >
                                                    <Images aria-hidden="true" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`admin-icon-button ${hidden ? "is-active" : ""}`}
                                                    title={hidden
                                                        ? tl("მარკეტზე გამოჩენა", "Show on the market", "Показать на маркете", "Ցուցադրել շուկայում")
                                                        : tl("მარკეტიდან დამალვა", "Hide from the market", "Скрыть с маркета", "Թաքցնել շուկայից")}
                                                    aria-label={hidden
                                                        ? tl("მარკეტზე გამოჩენა", "Show on the market", "Показать на маркете", "Ցուցադրել շուկայում")
                                                        : tl("მარკეტიდან დამალვა", "Hide from the market", "Скрыть с маркета", "Թաքցնել շուկայից")}
                                                    disabled={busy}
                                                    onClick={() => toggleHidden(car)}
                                                >
                                                    {hidden ? <Eye aria-hidden="true" /> : <EyeSlash aria-hidden="true" />}
                                                </button>
                                                {canDelete && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="admin-icon-button"
                                                            title={sold ? strings.mark_as_for_sale : strings.mark_as_sold}
                                                            aria-label={sold ? strings.mark_as_for_sale : strings.mark_as_sold}
                                                            disabled={busy}
                                                            onClick={() => toggleSold(car)}
                                                        >
                                                            <Tag aria-hidden="true" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="admin-icon-button is-danger"
                                                            title={common.delete}
                                                            aria-label={`${common.delete}: ${carTitle(car)}`}
                                                            disabled={busy}
                                                            onClick={() => setDeleteTarget(car)}
                                                        >
                                                            <Trash3 aria-hidden="true" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <nav className="admin-pagination" aria-label={marketStrings.page_of}>
                        <Pagination
                            count={totalPages}
                            page={Math.min(page, totalPages)}
                            onChange={(event, value) => setPage(value)}
                            showFirstButton
                            showLastButton
                            color="primary"
                            variant="outlined"
                            shape="rounded"
                        />
                    </nav>
                )}
            </>
        );
    };

    return (
        <AdminLayout
            title={strings.cars_title}
            subtitle={strings.cars_subtitle}
            actions={
                <button type="button" className="cx-btn cx-btn-primary" onClick={openCreate}>
                    <PlusLg aria-hidden="true" /> {strings.add_car}
                </button>
            }
        >
            <div className="admin-toolbar">
                <div className="admin-search">
                    <Search className="admin-search-icon" aria-hidden="true" />
                    <input
                        type="search"
                        className="cx-input admin-search-input"
                        placeholder={strings.search_cars}
                        aria-label={strings.search_cars}
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                    />
                </div>

                <select
                    className="cx-select admin-toolbar-select"
                    value={status}
                    aria-label={strings.table_status}
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                >
                    <option value="">{marketStrings.status_all}</option>
                    <option value="for_sale">{marketStrings.status_for_sale}</option>
                    <option value="sold">{marketStrings.status_sold}</option>
                    <option value="hidden">{tl("დამალული", "Hidden", "Скрытые", "Թաքցված")}</option>
                    <option value="visible">{tl("ხილული", "Visible", "Видимые", "Տեսանելի")}</option>
                </select>

                <span className="admin-toolbar-count">{total} {marketStrings.results_unit}</span>
            </div>

            {body()}

            <CarForm
                show={formOpen}
                car={editing}
                onClose={(didSave) => {
                    setFormOpen(false);
                    setEditing(null);
                    if (didSave) refresh();
                }}
                onSaved={() => refresh()}
            />

            <ConfirmModal
                show={Boolean(deleteTarget)}
                title={strings.delete_car_title}
                message={strings.delete_car_message}
                detail={deleteTarget ? carTitle(deleteTarget) : ""}
                confirmLabel={common.delete}
                loading={rowBusyId !== null && Boolean(deleteTarget)}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </AdminLayout>
    );
};

export default AdminCars;
